import { api } from '@/shared/axios-client';
import type { ScmProviderId } from '@/types/scm-provider';
import { scmAuthHeaders, scmGet, withScmProvider } from './scmClient';
import type { PipelinePlatform } from '@/types/pipeline-platform';

/**
 * GitHub onboarding API client.
 *
 * Talks to the provider-aware backend wrapper under `/api/scm/*`.
 * Every call receives the user-selected ``provider`` from the onboarding UI.
 */

// ── Shared envelope ────────────────────────────────────────────────────────
export interface ServiceResponse<T> {
  success: boolean;
  statusCode: number;
  message: string;
  data: T | null;
}

// ── Normalized item shapes (match backend GitHubService) ─────────────────────
export interface GithubOrganization {
  login: string;
  id?: number;
  name?: string;
  type: 'Organization' | 'User';
  avatarUrl?: string;
  url?: string;
}

export interface GithubRepository {
  id: number;
  name: string;
  fullName: string;
  private: boolean;
  defaultBranch: string;
  htmlUrl?: string;
  cloneUrl?: string;
}

export interface GithubBranch {
  name: string;
  commitSha?: string;
  isDefault: boolean;
  protected: boolean;
}

export interface GithubWorkflow {
  id: number;
  name: string;
  path: string;
  state: string;
}

export interface GithubWorkflowJob {
  name: string;
  source: 'workflow_run' | 'workflow_yaml';
}

export interface GithubEnvironment {
  name: string;
  id?: number;
}

export interface GithubDeployment {
  sha?: string;
  ref?: string;
  environment?: string;
  state?: string;
  createdAt?: string;
  source: 'deployment' | 'workflow_run' | 'branch_tip';
}

export interface GithubTestScriptCandidate {
  filePath: string;
  fileName: string;
  size?: number;
  selectedByDefault: boolean;
}

export interface GithubRepoFile {
  path: string;
  size: number;
  compressedSize: number;
  isDirectory: boolean;
  content: string;
}

export interface GithubTestScriptIndexResult {
  inventoryId?: string;
  status?: 'processing' | 'completed' | 'failed' | 'skipped';
  selectedCount?: number;
  inserted?: number;
  failed?: number;
  message?: string;
  buildId?: number;
  commitId?: string;
}

export interface GithubDetectResponse {
  success: boolean;
  message: string;
  onboardData?: {
    organizationId?: string;
    repositoryId?: string;
    branchId?: string;
  };
  repoFiles?: {
    repoResponse?: { files: GithubRepoFile[]; fileCount: number };
    extensionCounts?: Record<string, number>;
  };
  commitId?: string;
  errors?: string[] | null;
  testScriptIndex?: GithubTestScriptIndexResult | null;
}

// ── Request bodies ───────────────────────────────────────────────────────────
export interface GithubTestAutomationBody {
  location: 'same' | 'different' | 'none';
  testOrganization?: string;
  testRepository?: string;
  /** GitHub numeric repository id (not the repo name). */
  testRepoId?: string;
  testRepoUrl?: string;
  testBranchName?: string;
  testWorkflowId?: string;
  testWorkflowName?: string;
  testWorkflowJob?: string;
  testScriptsRootPaths?: string[];
  selectedScriptPaths?: string[];
  testPat?: string;
  /** Test CI platform selected after the test branch. */
  pipelinePlatform?: PipelinePlatform;
  jenkins?: {
    baseUrl: string;
    username: string;
    apiToken: string;
    jobName: string;
    jobUrl?: string;
  };
}

export interface GithubDetectBody {
  organization: string;
  repository: string;
  repoUrl: string;
  branch: string;
  commitId?: string;
  workflowId?: string;
  workflowName?: string;
  workflowJob?: string;
  environment?: string;
  onboardedBy: string;
  detectedTechnologies?: string[];
  frontendUrl?: string;
  backendUrl?: string;
  codeCredentials?: { pat: string };
  testAutomation?: GithubTestAutomationBody;
}

// ── Discovery calls ────────────────────────────────────────────────────────
export const getGithubOrganizations = (
  provider: ScmProviderId,
  pat: string,
): Promise<ServiceResponse<{ organizations: GithubOrganization[] }>> =>
  scmGet(provider, '/api/scm/organizations', pat);

export const getGithubRepositories = (
  provider: ScmProviderId,
  pat: string,
  organization: string,
): Promise<ServiceResponse<{ repositories: GithubRepository[] }>> =>
  api.post(
    '/api/scm/repositories',
    withScmProvider(provider, { organization }),
    scmAuthHeaders(pat),
  );

export const getGithubBranches = (
  provider: ScmProviderId,
  pat: string,
  organization: string,
  repository: string,
): Promise<
  ServiceResponse<{ branches: GithubBranch[]; defaultBranch: string }>
> =>
  api.post(
    '/api/scm/branches',
    withScmProvider(provider, { organization, repository }),
    scmAuthHeaders(pat),
  );

export const getGithubWorkflows = (
  provider: ScmProviderId,
  pat: string,
  organization: string,
  repository: string,
): Promise<ServiceResponse<{ workflows: GithubWorkflow[] }>> =>
  api.post(
    '/api/scm/workflows',
    withScmProvider(provider, { organization, repository }),
    scmAuthHeaders(pat),
  );

export const getGithubWorkflowJobs = (
  provider: ScmProviderId,
  pat: string,
  organization: string,
  repository: string,
  workflowId: string,
  branch?: string,
): Promise<ServiceResponse<{ jobs: GithubWorkflowJob[] }>> =>
  api.post(
    '/api/scm/workflow-jobs',
    withScmProvider(provider, { organization, repository, workflowId, branch }),
    scmAuthHeaders(pat),
  );

export const getGithubEnvironments = (
  provider: ScmProviderId,
  pat: string,
  organization: string,
  repository: string,
): Promise<ServiceResponse<{ environments: GithubEnvironment[] }>> =>
  api.post(
    '/api/scm/environments',
    withScmProvider(provider, { organization, repository }),
    scmAuthHeaders(pat),
  );

export const getGithubLatestDeployment = (
  provider: ScmProviderId,
  pat: string,
  organization: string,
  repository: string,
  opts: { environment?: string; branch?: string; workflowId?: string } = {},
): Promise<ServiceResponse<{ deployment: GithubDeployment | null }>> =>
  api.post(
    '/api/scm/latest-deployment',
    withScmProvider(provider, { organization, repository, ...opts }),
    scmAuthHeaders(pat),
  );

export const getGithubTestScripts = (
  provider: ScmProviderId,
  pat: string,
  organization: string,
  repository: string,
  branch: string,
  opts: { workflowId?: string; testScriptsRootPaths?: string[] } = {},
): Promise<
  ServiceResponse<{
    candidates: GithubTestScriptCandidate[];
    summary?: Record<string, unknown>;
  }>
> =>
  api.post(
    '/api/scm/test-scripts',
    withScmProvider(provider, { organization, repository, branch, ...opts }),
    scmAuthHeaders(pat),
  );

// ── Onboard / detect ─────────────────────────────────────────────────────────
export const detectGithubRepository = (
  provider: ScmProviderId,
  body: GithubDetectBody,
  pat: string,
): Promise<GithubDetectResponse> =>
  api.post(
    '/api/scm/detect',
    withScmProvider(provider, body),
    scmAuthHeaders(pat),
  );
